import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { Container } from '@pixi/react';
import { shallowEqual } from 'react-redux';
import { useAppSelector } from '../../../redux/store';
import { DARKEST_GREENISH_BLUE_NUM, SIDE_BET_INCOMPLETE_DOT_COLOR_NUM, SIDE_BET_LOST_DOT_COLOR_NUM, SIDE_BET_PENDING_DOT_COLOR_NUM, SIDE_BET_WON_DOT_COLOR_NUM } from '../../../styles/colors';
import { assertUnreachable } from '../../../utils/tsUtils';
import { isTestEnvironment, range } from '../../../utils/utils';
import { Circle } from '../pixiComponents/circle';
const DOT_COLORS = {
    won: SIDE_BET_WON_DOT_COLOR_NUM,
    lost: SIDE_BET_LOST_DOT_COLOR_NUM,
    pending: SIDE_BET_PENDING_DOT_COLOR_NUM,
    incomplete: SIDE_BET_INCOMPLETE_DOT_COLOR_NUM
};
const DOT_DIAMETER_PX = 6;
const DOT_RADIUS_PX = DOT_DIAMETER_PX / 2;
const DOT_GAP_PX = 2;
const SPACE_BETWEEN_DOT_CENTERS_PX = DOT_DIAMETER_PX + DOT_GAP_PX;
/**
 * The number of dots to show may be less than the actual number of side bets if there is not enough room to show all of them.
 */
function getNumSideBetDots(numSideBets, bounds) {
    if (!numSideBets) {
        return;
    }
    if (bounds.w < DOT_DIAMETER_PX) {
        return;
    }
    const maxNumSideBetDots = (bounds.w - DOT_DIAMETER_PX) / SPACE_BETWEEN_DOT_CENTERS_PX;
    return Math.min(numSideBets, maxNumSideBetDots);
}
/**
 * Renders little dots under a player's name that has side bets placed on them.
 * The number of dots indicate the number of side bets on them (space permitting).
 */
export const PlayerSideBetDotsContainer = ({ seatIndex, bounds }) => {
    const isSideBettingSettingEnabled = useAppSelector((state) => state.selfData.isSideBettingSettingEnabled);
    const numSideBets = useAppSelector((state) => {
        if (!isSideBettingSettingEnabled) {
            return;
        }
        if (!state.sideBets.sideBetItems.length) {
            return;
        }
        const seatedPlayer = state.poker.seatedPlayers[seatIndex];
        if (!seatedPlayer) {
            return;
        }
        return state.sideBets.sideBetItems.filter(({ targetPlayer }) => targetPlayer.id === seatedPlayer.id).length;
    });
    const usernameBarBounds = useAppSelector((state) => state.gameDimensions.usernameBarBounds, shallowEqual);
    const dotType = useAppSelector((state) => {
        if (!isSideBettingSettingEnabled) {
            return;
        }
        switch (state.sideBets.sideBetsHandResult.type) {
            case 'pending': {
                return 'pending';
            }
            case 'incomplete': {
                return 'incomplete';
            }
            case 'complete': {
                const seatedPlayer = state.poker.seatedPlayers[seatIndex];
                return state.sideBets.sideBetsHandResult.handWinners.some(({ id }) => id === seatedPlayer?.id) ? 'won' : 'lost';
            }
            default: {
                assertUnreachable(state.sideBets.sideBetsHandResult);
            }
        }
    });
    if (!isSideBettingSettingEnabled || !usernameBarBounds || !dotType) {
        return null;
    }
    const numSideBetDots = getNumSideBetDots(numSideBets, bounds);
    if (!numSideBetDots) {
        return null;
    }
    const leftmostDotX = -((numSideBetDots - 1) * SPACE_BETWEEN_DOT_CENTERS_PX) / 2;
    return (_jsx(Container, { x: 0, y: usernameBarBounds.y + usernameBarBounds.h / 2 + 2, interactiveChildren: false, children: range(numSideBetDots).map((i) => {
            const x = leftmostDotX + i * SPACE_BETWEEN_DOT_CENTERS_PX;
            const pixiData = isTestEnvironment() ? { type: 'side-bet-dot', seatIndex, dotType, dotIndex: i } : null;
            return (_jsxs(Container, { x: x, y: 0, "pixi-data": pixiData, children: [_jsx(Circle, { radius: DOT_RADIUS_PX + 2, x: 0, y: 0, fillColor: DARKEST_GREENISH_BLUE_NUM }), _jsx(Circle, { radius: DOT_RADIUS_PX, x: 0, y: 0, fillColor: DOT_COLORS[dotType] })] }, i));
        }) }));
};
